import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Paper, Typography, Box, TextField, Alert, CircularProgress
} from '@mui/material';
// Change the import to use the UnifiedContainerSelector instead
import UnifiedContainerSelector from '../../tree/UnifiedContainerSelector';

/**
 * TestCaseDialog component - Shows AI generated test cases and allows editing and selection
 * 
 * @component
 */
const TestCaseDialog = ({
  open,
  onClose,
  data,
  onCreateTestCases
}) => {
  const [selectedTests, setSelectedTests] = useState([]);
  const [editedTests, setEditedTests] = useState([]);
  const [showContainerSelector, setShowContainerSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Initialize when dialog opens or data changes
  useEffect(() => {
    if (data === null) {
      // Still loading
      setLoading(true);
      setEditedTests([]);
      setSelectedTests([]);
    } else if (data && data.tests) {
      // Data loaded successfully
      setLoading(false);
      // Initialize edited tests with original data. `_key` is a React `key=`
      // identity only, stamped once here — the row set is fixed for this dialog's
      // lifetime (no add/remove UI; rows are only ever edited or selected in
      // place, by index), so a stable identity per row avoids inputs losing
      // focus/value across re-renders without needing the array index the rule
      // flags.
      const initialEditedTests = data.tests.map(test => ({
        ...test,
        selected: true, // Select all by default
        _key: uuidv4()
      }));
      setEditedTests(initialEditedTests);
      // Select all by default
      setSelectedTests([...Array(initialEditedTests.length).keys()]);
    } else {
      // Data loaded but no tests or error
      setLoading(false);
      setEditedTests([]);
      setSelectedTests([]);
    }
  }, [data]);
  
  // Toggle test selection
  const handleToggleSelection = (index) => {
    setSelectedTests(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      return [...prev, index];
    });
    
    setEditedTests(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected: !updated[index].selected };
      return updated;
    });
  };
  
  // Handle title edit
  const handleTitleChange = (index, value) => {
    setEditedTests(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], title: value };
      return updated;
    });
  };
  
  // Handle test steps edit
  const handleStepsChange = (index, value) => {
    setEditedTests(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], description: value };
      return updated;
    });
  };
  
  // Handle create button
  const handleCreate = () => {
    // Open container selector
    setShowContainerSelector(true);
  };
  
  // Handle container selection
  const handleContainerSelected = (containerId, containerName) => {
    // Log what we received
    console.log('Container selected:', containerId, containerName);
    
    // Make sure containerId is defined
    if (!containerId) {
      console.error('Container ID is undefined');
      return;
    }
    
    // Get selected tests
    const testsToCreate = editedTests
      .filter((_, index) => selectedTests.includes(index));
    
    if (testsToCreate.length === 0) {
      console.warn('No test cases selected');
      return;
    }
    
    // Use containerName for logging purposes
    console.log(`Creating ${testsToCreate.length} test case(s) in container: ${containerName}`);
    
    // Call create function
    onCreateTestCases(testsToCreate, containerId);
    
    // Close dialog
    setShowContainerSelector(false);
    onClose();
  };
  
  return (
    <>
      <Dialog
        open={open && !showContainerSelector}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Generated Test Cases</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : !editedTests.length ? (
            <Alert severity="info">
              No test cases were generated. Try again with a more detailed requirement.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" component="p" sx={{ mb: 2 }}>
                Select which test cases you want to create. You can edit them before creation.
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom>
                Selected: {selectedTests.length}/{editedTests.length} test cases
              </Typography>
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox 
                          checked={selectedTests.length === editedTests.length}
                          indeterminate={selectedTests.length > 0 && 
                                         selectedTests.length < editedTests.length}
                          onChange={() => {
                            if (selectedTests.length === editedTests.length) {
                              // Deselect all
                              setSelectedTests([]);
                              setEditedTests(prev => prev.map(test => ({...test, selected: false})));
                            } else {
                              // Select all
                              setSelectedTests([...Array(editedTests.length).keys()]);
                              setEditedTests(prev => prev.map(test => ({...test, selected: true})));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Test Case Title</TableCell>
                      <TableCell>Test Steps</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedTests.map((test, index) => (
                      <TableRow key={test._key}>
                        <TableCell padding="checkbox">
                          <Checkbox 
                            checked={selectedTests.includes(index)}
                            onChange={() => handleToggleSelection(index)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={test.title}
                            onChange={(e) => handleTitleChange(index, e.target.value)}
                            disabled={!selectedTests.includes(index)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={8}
                            variant="outlined"
                            size="small"
                            value={test.description}
                            onChange={(e) => handleStepsChange(index, e.target.value)}
                            disabled={!selectedTests.includes(index)}
                            sx={{
                              '& .MuiInputBase-input': {
                                overflow: 'auto'
                              },
                              '& .MuiOutlinedInput-root': {
                                height: 'auto'
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleCreate}
            variant="contained" 
            color="primary"
            disabled={selectedTests.length === 0}
          >
            Create Test Cases
          </Button>
        </DialogActions>
      </Dialog>
      
      {showContainerSelector && (
        <UnifiedContainerSelector
          open={showContainerSelector}
          onClose={() => setShowContainerSelector(false)}
          onSelect={handleContainerSelected}
          showCreateOption={true}
          title="Select Container for Test Cases"
          fallbackToLegacy={true}
        />
      )}
    </>
  );
};

TestCaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  onCreateTestCases: PropTypes.func.isRequired
};

export default TestCaseDialog;